import React, { useState, useEffect } from "react";
import { Table, Spin, Button, Modal, Select, Switch, Space } from "antd";
import {
  CheckOutlined,
  CloseOutlined,
  DeleteOutlined,
  PlusOutlined,
} from "@ant-design/icons";
import { useMercuryContext } from "../../../context";
import {
  failedNotification,
  successfulNotification,
} from "../../../common/utils/notifications";
import type { ColumnsType } from "antd/es/table";
import PermissionWrapper from "shared/components/PermissionWrapper";
import { checkPermissions } from "common/utils/checkPermissions";
import "./line-item-definitions-tab.scss"
interface ManageTypeLineItemsProps {
  typeId?: string;
}

interface LineItemAssociation {
  _id: string;
  id: string;
  invoicingTypeId: string;
  lineItemDefinitionId: string;
  defaultQuantity: number;
  overrideDescription?: string;
  sortOrder: number;
  required: boolean;
  active: boolean;
  deleted: boolean;
}

interface LineItemDefinition {
  _id: string;
  id: string;
  name: string;
  description?: string;
  rateType?: string;
  varServiceType?: string;
  category?: string;
  active: boolean;
}

const ManageTypeLineItems: React.FC<ManageTypeLineItemsProps> = ({
  typeId,
}) => {
  const [data, setData] = useState<LineItemAssociation[]>([]);
  const [definitions, setDefinitions] = useState<LineItemDefinition[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [viewDeleted, setViewDeleted] = useState(false);

  const [isVisibleAdd, setIsVisibleAdd] = useState(false);
  const [isVisibleRemove, setIsVisibleRemove] = useState(false);

  const [selectedDefinitionIds, setSelectedDefinitionIds] = useState<string[]>(
    []
  );
  const [selectedItem, setSelectedItem] =
    useState<LineItemAssociation | null>(null);

  const context = useMercuryContext();
  const { invoicingClient } = context;

  const userPermissions = context?.userDetails?.permissions || [];
  const canCreate = checkPermissions(userPermissions, [
    {
      customerApplication: "Mercury",
      permissionName: "Invoice Types",
      action: "create",
    },
  ]);
  const canDelete = checkPermissions(userPermissions, [
    {
      customerApplication: "Mercury",
      permissionName: "Invoice Types",
      action: "delete",
    },
  ]);

  const fetchLineItems = async () => {
    if (!typeId) return;
    try {
      setIsLoading(true);
      const res = await invoicingClient.get(
        `invoicing-v1/types/${typeId}/line-items?includeDeleted=${viewDeleted}`
      );
      setData(res.data?.data || res.data || []);
    } catch (e) {
      console.error(e);
    } finally {
      setIsLoading(false);
    }
  };

  const fetchDefinitions = async () => {
    try {
      const res = await invoicingClient.get(
        `invoicing-v1/line-item-definitions`
      );
      setDefinitions(res.data?.data || res.data || []);
    } catch (e) {
      console.error(e);
    }
  };

  useEffect(() => {
    fetchLineItems();
  }, [typeId, viewDeleted]);

  useEffect(() => {
    fetchDefinitions();
  }, []);

  const getDefinitionName = (lineItemDefinitionId: string) => {
    const def = definitions.find(
      (d) => d.id === lineItemDefinitionId || d._id === lineItemDefinitionId
    );
    return def?.name ?? lineItemDefinitionId;
  };

  const getDefinition = (lineItemDefinitionId: string) => {
    return definitions.find(
      (d) => d.id === lineItemDefinitionId || d._id === lineItemDefinitionId
    );
  };

  const alreadyAssociatedIds = data
    .filter((a) => !a.deleted)
    .map((a) => a.lineItemDefinitionId);

  const availableDefinitions = definitions.filter(
    (d) =>
      d.active &&
      !alreadyAssociatedIds.includes(d.id) &&
      !alreadyAssociatedIds.includes(d._id)
  );

  const tableColumns: ColumnsType<LineItemAssociation> = [
    {
      title: "Line Item Definition",
      dataIndex: "lineItemDefinitionId",
      width: 200,
      render: (id: string) => getDefinitionName(id),
    },
    {
      title: "Category",
      width: 130,
      render: (_, r) => getDefinition(r.lineItemDefinitionId)?.category || "-",
    },
    {
      title: "Quantity",
      dataIndex: "defaultQuantity",
      width: 100,
    },
    {
      title: "Sort Order",
      dataIndex: "sortOrder",
      width: 100,
    },
    {
      title: "Required",
      align: "center",
      width: 80,
      render: (_, r) =>
        r.required ? (
          <CheckOutlined style={{ color: "var(--tertiary-color)" }} />
        ) : (
          <CloseOutlined style={{ color: "var(--red)" }} />
        ),
    },
    ...(!viewDeleted
      ? [
        {
          title: "",
          width: 90,
          render: (_: any, r: LineItemAssociation) => (
            <PermissionWrapper hasPermissions={canDelete}>
              <Button
                type="link"
                size="small"
                danger
                disabled={!canDelete}
                icon={<DeleteOutlined />}
                onClick={() => {
                  setSelectedItem(r);
                  setIsVisibleRemove(true);
                }}
              >
                Remove
              </Button>
            </PermissionWrapper>
          ),
        },
      ]
      : []),
  ];

  const handleAddDefinitions = async () => {
    if (!typeId || !selectedDefinitionIds.length) return;
    try {
      await Promise.all(
        selectedDefinitionIds.map((defId, index) =>
          invoicingClient.post(`invoicing-v1/types/${typeId}/line-items`, {
            invoicingTypeId: typeId,
            lineItemDefinitionId: defId,
            defaultQuantity: 0,
            sortOrder: data.length + index,
            required: false,
            active: true,
            deleted: false,
          })
        )
      );
      successfulNotification(
        "Success",
        `${selectedDefinitionIds.length} line item definition(s) added`
      );
      setIsVisibleAdd(false);
      setSelectedDefinitionIds([]);
      fetchLineItems();
    } catch {
      failedNotification("Error", "Failed to add line item definitions");
    }
  };

  const handleConfirmRemove = async () => {
    if (!selectedItem) return;
    try {
      await invoicingClient.delete(
        `invoicing-v1/types/line-items/${selectedItem._id || selectedItem.id}`
      );
      successfulNotification("Success", "Line item definition removed");
      setIsVisibleRemove(false);
      setSelectedItem(null);
      fetchLineItems();
    } catch {
      failedNotification("Error", "Failed to remove line item definition");
    }
  };

  if (isLoading) {
    return (
      <div style={{ textAlign: "center", padding: 20 }}>
        <Spin size="small" /> Loading line items...
      </div>
    );
  }

  const visibleData = viewDeleted
    ? data.filter((i) => Boolean(i.deleted))
    : data.filter((i) => !i.deleted);

  return (
    <div>
      <div style={{ display: "flex", alignItems: "center", padding: "0 16px 8px 16px" }}>
        <PermissionWrapper hasPermissions={canCreate}>
          <Button
            type="primary"
            icon={<PlusOutlined />}
            disabled={!canCreate}
            onClick={() => {
              setSelectedDefinitionIds([]);
              setIsVisibleAdd(true);
            }}
          >
            Add Line Item Definition
          </Button>
        </PermissionWrapper>

        <Space style={{ marginLeft: 16 }}>
          <span>View Deleted</span>
          <Switch
            checked={viewDeleted}
            onChange={setViewDeleted}
            size="small"
          />
        </Space>
      </div>

      <Table
        columns={tableColumns}
        dataSource={visibleData}
        rowKey={(r) => r._id || r.id}
        size="small"
        pagination={{ pageSize: 20 }}
        className="line-item-definitions-table"
      />

      <Modal
        title="Add Line Item Definitions"
        open={isVisibleAdd}
        onOk={handleAddDefinitions}
        okButtonProps={{ disabled: !selectedDefinitionIds.length }}
        onCancel={() => setIsVisibleAdd(false)}
      >
        <p>
          Select existing line item definitions to associate with this invoice
          type:
        </p>
        <Select
          mode="multiple"
          style={{ width: "100%" }}
          placeholder="Search and select definitions"
          showSearch
          optionFilterProp="label"
          value={selectedDefinitionIds}
          onChange={setSelectedDefinitionIds}
          options={availableDefinitions.map((d) => ({
            value: d.id || d._id,
            label: d.name,
          }))}
        />
      </Modal>

      <Modal
        title="Remove Line Item Definition"
        open={isVisibleRemove}
        onOk={handleConfirmRemove}
        okButtonProps={{ danger: true }}
        onCancel={() => setIsVisibleRemove(false)}
      >
        Are you sure you want to remove{" "}
        <strong>
          {selectedItem
            ? getDefinitionName(selectedItem.lineItemDefinitionId)
            : ""}
        </strong>{" "}
        from this invoice type?
      </Modal>
    </div>
  );
};

export default ManageTypeLineItems;
